import React, { useEffect, useState } from 'react';
import { Attendance, AttendanceResponse } from '../../interfaces/attendance.interface';
import { AttendanceService } from '../../services/attendance.service';
import { LeaveApplicationResponse, LeaveStatus } from '../../interfaces/leave.interface';
import { LeaveService } from '../../services/leave.service';
import { HolidayResponse } from '../../interfaces/holiday.interface';
import { HolidayService } from '../../services/holiday.service';
import { UserType } from '../../interfaces/userType.enum';
import {
    Box, Checkbox, Chip, FormControlLabel,
    Typography, Button, Grid, TextField,
    IconButton, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import { useShiftLookup } from '../../hooks/useShiftLookup';
import { Edit } from '@mui/icons-material';
import { format, isWithinInterval, parseISO, eachDayOfInterval, startOfDay, endOfDay, subDays } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { EmployeeAutocomplete } from '../common/EmployeeAutocomplete';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { FALLBACK_WEEKEND } from '../../services/institutionSettings.service';
import { humanizeEnum } from '../../utils/format';
import { WeekDay } from '../../interfaces/institutionSettings.interface';
import { AuthService } from '../../services/auth.service';
import { WorkSiteService } from '../../services/workSite.service';
import { Column } from '../common/responsiveTable.types';
import ResponsiveTable from '../common/ResponsiveTable';
import AttendanceStatusChip from './AttendanceStatusChip';
import LocationStatusChip from './LocationStatusChip';

import SearchableSelect from '../common/SearchableSelect';
const JS_DAY_TO_NAME: WeekDay[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

interface User {
    id: string;
    username: string;
    type: UserType;
}

/** One rendered line: a date in range, plus the attendance record for it if there is one. */
interface EmployeeAttendanceRow {
    /** Raw yyyy-MM-dd key from `dateRange` — stable and unique, unlike the display-formatted date. */
    rawDate: string;
    date: string;
    day: string;
    status: string;
    attendance?: Attendance;
    /** Absent when the user lacks ATTENDANCE_MANAGE (the PUT endpoint's key): no edit icon. */
    onEdit?: (attendance: Attendance) => void;
}

const formatTimeCol = (dateString: string) => {
    if (!dateString) return '-';
    return format(parseISO(dateString), 'HH:mm');
};

/** Clock-in stamps endTime = startTime, so an equal end means "not clocked out yet". */
export const formatEndTimeCol = (startStr: string, endStr: string): string => {
    if (!endStr || endStr === startStr) return '—';
    return formatTimeCol(endStr);
};

const formatDurationCol = (startStr: string, endStr: string): string => {
    if (!startStr || !endStr) return '-';
    const diffMs = parseISO(endStr).getTime() - parseISO(startStr).getTime();
    if (diffMs <= 0) return '-';
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
};

// Declared in the OLD table's source order (Date, Day, Start Time, End Time, Total Time, Status,
// Shift, Instance Status, Late, OT, Actions) — `priority` only picks the card/compact LEAD, it
// never reorders table mode.
export const employeeAttendanceColumns: Column<EmployeeAttendanceRow>[] = [
    { key: 'date', header: 'Date', priority: 1, helpKey: 'attendance.employee.col.date', render: r => r.date },
    { key: 'day', header: 'Day', helpKey: 'attendance.employee.col.day', render: r => r.day },
    { key: 'startTime', header: 'Start Time', cardLabel: 'In', helpKey: 'attendance.employee.col.startTime', render: r => r.attendance ? formatTimeCol(r.attendance.startTime) : '-' },
    { key: 'endTime', header: 'End Time', helpKey: 'attendance.employee.col.endTime', cardLabel: 'Out', render: r => r.attendance ? formatEndTimeCol(r.attendance.startTime, r.attendance.endTime) : '-' },
    { key: 'totalTime', header: 'Total Time', cardLabel: 'Total', helpKey: 'attendance.employee.col.totalTime', render: r => r.attendance ? formatDurationCol(r.attendance.startTime, r.attendance.endTime) : '-' },
    { key: 'status', header: 'Status', priority: 1, helpKey: 'attendance.employee.col.status', render: r => <AttendanceStatusChip status={r.status} /> },
    { key: 'shift', header: 'Shift', helpKey: 'attendance.employee.col.shift', render: r => r.attendance?.shiftCode ? <Chip label={r.attendance.shiftCode} size="small" /> : '-' },
    { key: 'instanceStatus', header: 'Instance Status', helpKey: 'attendance.employee.col.instanceStatus', render: r => r.attendance?.instanceStatus ? humanizeEnum(r.attendance.instanceStatus) : '-' },
    { key: 'late', header: 'Late', helpKey: 'attendance.employee.col.late', render: r => r.attendance?.isLate ? <Chip label={`${r.attendance.lateMinutes}m`} size="small" color="error" /> : '-' },
    { key: 'overtime', header: 'OT', helpKey: 'attendance.employee.col.overtime', render: r => (r.attendance?.overtimeMinutes ?? 0) > 0 ? <Chip label={`${r.attendance!.overtimeMinutes}m`} size="small" /> : '-' },
    {
        key: 'actions', header: 'Actions', helpKey: 'attendance.employee.col.actions', role: 'actions', render: r => r.attendance && r.onEdit && (
            <Tooltip title="Edit Attendance" arrow>
                <IconButton
                    data-help="attendance.employee.action.edit"
                    size="small"
                    onClick={() => r.onEdit!(r.attendance!)}
                    color="primary"
                    aria-label="Edit Attendance"
                >
                    <Edit />
                </IconButton>
            </Tooltip>
        ),
    },
];

/**
 * The Location column, built per render because it closes over the work-site name lookup.
 *
 * Not in `employeeAttendanceColumns` itself: that array is module-scope and unconditional, while
 * this column is only ever shown when the loaded rows actually carry location data.
 */
const locationColumn = (siteNamesById: Map<string, string>): Column<EmployeeAttendanceRow> => ({
    key: 'location',
    header: 'Location',
    helpKey: 'attendance.employee.col.location',
    render: r => r.attendance ? (
        <LocationStatusChip
            status={r.attendance.locationStatus}
            distanceMeters={r.attendance.distanceMeters}
            siteName={r.attendance.workSiteId ? siteNamesById.get(r.attendance.workSiteId) : undefined}
            latitude={r.attendance.latitude}
            longitude={r.attendance.longitude}
            accuracyMeters={r.attendance.locationAccuracyMeters}
            isMock={r.attendance.isMockLocation}
        />
    ) : null,
});

/**
 * The column model for one render: the eleven fixed columns, plus Location when the loaded rows
 * carry location data.
 *
 * Location goes in as the last DATA column — immediately before `actions`, exactly where the
 * old markup put it. Appending it after `actions` would leave the edit affordance stranded
 * mid-table in table mode, which is the one place column position is still visible. No
 * pre-existing column moves relative to any other.
 *
 * `employeeAttendanceColumns` stays exported and unconditional so the parity test can keep
 * asserting the ordered eleven-key list — the thing that guards against a silent reorder. This
 * factory is what the page renders, and is pinned separately in both directions.
 */
export const employeeAttendanceColumnsFor = (
    hasLocationData: boolean,
    siteNamesById: Map<string, string>,
): Column<EmployeeAttendanceRow>[] => {
    if (!hasLocationData) return employeeAttendanceColumns;
    const actionsIndex = employeeAttendanceColumns.findIndex(c => c.role === 'actions');
    if (actionsIndex < 0) return [...employeeAttendanceColumns, locationColumn(siteNamesById)];
    return [
        ...employeeAttendanceColumns.slice(0, actionsIndex),
        locationColumn(siteNamesById),
        ...employeeAttendanceColumns.slice(actionsIndex),
    ];
};

const EmployeeAttendanceList: React.FC = () => {
    const canManageAttendance = usePermission(PERMISSIONS.ATTENDANCE_MANAGE);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [attendanceData, setAttendanceData] = useState<AttendanceResponse | null>(null);
    const [leaveData, setLeaveData] = useState<LeaveApplicationResponse | null>(null);
    const [holidayData, setHolidayData] = useState<HolidayResponse | null>(null);
    const [dateRange, setDateRange] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingAttendance, setEditingAttendance] = useState<Attendance | null>(null);
    const [editStartTime, setEditStartTime] = useState('');
    const [editEndTime, setEditEndTime] = useState('');
    // A night shift clocks out after midnight: the end time then falls on the day after the record date.
    const [editEndsNextDay, setEditEndsNextDay] = useState(false);
    const [saving, setSaving] = useState(false);
    const [weekendDays, setWeekendDays] = useState<WeekDay[]>(FALLBACK_WEEKEND);
    const [selectedShiftId, setSelectedShiftId] = useState<string>('');
    const toast = useToast();
    const { shifts } = useShiftLookup();
    const [siteNamesById, setSiteNamesById] = useState<Map<string, string>>(new Map());
    const [flaggedOnly, setFlaggedOnly] = useState(false);
    // A department head may only read their team's attendance (the server 403s anyone else),
    // so the picker offers only that team. null = no restriction; the picker stays disabled
    // until the roster answers so the whole institution never flashes into the list.
    const [allowedIds, setAllowedIds] = useState<ReadonlySet<string> | null>(null);
    const [rosterLoaded, setRosterLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // A failed roster read keeps the previous (unfiltered) picker rather than blocking the page,
        // same as InstitutionTodayAttendance.
        Promise.resolve()
            .then(() => AttendanceService.getInstitutionTodayRoster())
            .catch(() => null)
            .then(roster => {
                if (cancelled) return;
                setAllowedIds(roster?.restricted ? new Set(roster.userIds) : null);
                setRosterLoaded(true);
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        // Fetched once and reused for every row. WorkSiteService.list is paginated
        // and clamped to 100 by the client service; an institution with more than
        // 100 sites will not have every name resolved here. Rows referencing an
        // unresolved site fall back to a distance-only label rather than a raw id.
        const fetchSiteNames = async () => {
            try {
                const page = await WorkSiteService.list(0, 100);
                setSiteNamesById(new Map(page.content.map(site => [site.id as string, site.name])));
            } catch {
                // Non-fatal: rows simply fall back to a distance-only location label.
            }
        };
        fetchSiteNames();
    }, []);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const institutionId = AuthService.getSelectedInstitution();
                const [leaveResponse, holidayResponse, weekend] = await Promise.all([
                    LeaveService.getAllApplicationsByInstitution(0, 500),
                    HolidayService.getHolidaysByInstitution(),
                    // Weekend days are display-only and member-readable; on failure fall back to Friday
                    // only, silently, instead of failing the whole load.
                    institutionId
                        ? InstitutionSettingsService.getWeekendDays(institutionId).catch(() => null)
                        : Promise.resolve(null),
                ]);
                setLeaveData(leaveResponse);
                setHolidayData(holidayResponse);
                if (weekend && weekend.length > 0) {
                    setWeekendDays(weekend);
                }
            } catch (err) {
                toast.showApiError(err, 'Failed to fetch data');
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            const range = eachDayOfInterval({
                start: startOfDay(startDate),
                end: endOfDay(endDate)
            });
            setDateRange(range.map(d => format(d, 'yyyy-MM-dd')).reverse());
        }
    }, [startDate, endDate]);

    const fetchAttendance = async () => {
        if (!selectedUser) {
            toast.showError('Please select an employee');
            return;
        }
        try {
            setLoading(true);
            const data = await AttendanceService.getEmployeeAttendanceList(selectedUser.id, startDate, endDate);
            setAttendanceData(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch attendance');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const getStatus = (date: string): string => {
        const dayName = JS_DAY_TO_NAME[parseISO(date).getDay()];
        if (weekendDays.includes(dayName)) {
            return 'Holiday: Weekend';
        }

        if (attendanceData?.content.some(a => a.date === date)) {
            return 'Present';
        }

        const leaveApplication = leaveData?.content.find(leave =>
            leave.userId === selectedUser?.id &&
            leave.leaveStatus === LeaveStatus.Approved &&
            isWithinInterval(parseISO(date), {
                start: parseISO(leave.startDate),
                end: parseISO(leave.endDate)
            })
        );
        if (leaveApplication) {
            return leaveApplication.leaveType;
        }

        const holiday = holidayData?.content.find(h =>
            isWithinInterval(parseISO(date), {
                start: parseISO(h.startDate),
                end: parseISO(h.endDate)
            })
        );
        if (holiday) {
            return `Holiday: ${holiday.name}`;
        }

        return 'Absent';
    };

    const handleOpenEditDialog = (attendance: Attendance) => {
        setEditingAttendance(attendance);
        setEditStartTime(format(parseISO(attendance.startTime), 'HH:mm'));
        setEditEndTime(format(parseISO(attendance.endTime), 'HH:mm'));
        setEditEndsNextDay(format(parseISO(attendance.endTime), 'yyyy-MM-dd') > attendance.date);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingAttendance(null);
    };

    const handleSaveEdit = async () => {
        if (!editingAttendance) return;
        try {
            setSaving(true);
            const date = editingAttendance.date;
            const startTime = new Date(`${date}T${editStartTime}:00`).toISOString();
            const end = new Date(`${date}T${editEndTime}:00`);
            if (editEndsNextDay) end.setDate(end.getDate() + 1);
            const endTime = end.toISOString();
            await AttendanceService.updateAttendance(editingAttendance.id, startTime, endTime);
            toast.showSuccess('Attendance updated successfully');
            handleCloseEditDialog();
            await fetchAttendance();
        } catch (err) {
            toast.showApiError(err, 'Failed to update attendance');
        } finally {
            setSaving(false);
        }
    };

    const getAttendanceByDate = (date: string) =>
        attendanceData?.content.find(a => a.date === date);

    // Hide the whole column when no row on the page has a locationStatus, so a
    // tenant that never enabled the feature sees exactly the table they see today.
    const hasLocationData = !!attendanceData?.content.some(a => !!a.locationStatus);

    const rows: EmployeeAttendanceRow[] = dateRange
        .filter(date => {
            if (!selectedShiftId) return true;
            const attendance = getAttendanceByDate(date);
            return attendance?.shiftId === selectedShiftId;
        })
        .filter(date => {
            // Gated on hasLocationData, not on flaggedOnly alone. The
            // checkbox only renders when the loaded rows carry location
            // data, so changing the date range to a span with none
            // unmounts it while flaggedOnly stays true — every row would
            // then be filtered out with no visible control to undo it,
            // and only a reload would recover.
            if (!flaggedOnly || !hasLocationData) return true;
            const attendance = getAttendanceByDate(date);
            // A simulated-location punch is the single most suspicious signal
            // in the system — it must never be filtered out of the flagged view,
            // even when the fabricated coordinates land INSIDE the fence.
            return attendance?.locationStatus === 'OUTSIDE' || !!attendance?.isMockLocation;
        })
        .map(date => ({
            rawDate: date,
            date: formatDate(date),
            day: format(parseISO(date), 'EEEE'),
            status: getStatus(date),
            attendance: getAttendanceByDate(date),
            onEdit: canManageAttendance ? handleOpenEditDialog : undefined,
        }));

    return (
        <Box data-help-page="attendance.employee.page" sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
                Employee Attendance
            </Typography>

            <Grid data-help-section="attendance.employee.filters" container spacing={2} sx={{ marginBottom: 3 }}>
                <Grid>
                    <EmployeeAutocomplete<User>
                        data-help="attendance.employee.picker"
                        sx={{ minWidth: 220 }}
                        size="medium"
                        value={selectedUser}
                        allowedIds={allowedIds}
                        disabled={!rosterLoaded}
                        onChange={(newValue) => {
                            setSelectedUser(newValue);
                            setAttendanceData(null);
                        }}
                        onError={(err) => toast.showApiError(err, 'Failed to search employees')}
                    />
                </Grid>
                <Grid>
                    <TextField
                        data-help="attendance.employee.startDate"
                        label="Start Date"
                        type="date"
                        value={format(startDate, 'yyyy-MM-dd')}
                        onChange={(e) => setStartDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid>
                    <TextField
                        data-help="attendance.employee.endDate"
                        label="End Date"
                        type="date"
                        value={format(endDate, 'yyyy-MM-dd')}
                        onChange={(e) => setEndDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid>
                    <SearchableSelect
                        options={shifts.filter(s => s.isActive)}
                        value={selectedShiftId}
                        onChange={(id) => setSelectedShiftId(id)}
                        getOptionLabel={s => `${s.code} – ${s.name}`}
                        data-help="attendance.employee.shift"
                        label="Shift"
                        placeholder="All shifts"
                        sx={{ minWidth: 200 }}
                    />
                </Grid>
                <Grid>
                    <Button data-help="attendance.employee.apply"
                        variant="contained"
                        color="primary"
                        onClick={fetchAttendance}
                        disabled={loading}
                        sx={{ mt: { xs: 0, md: 1 } }}
                    >
                        Apply Filters
                    </Button>
                </Grid>
                {hasLocationData && (
                    <Grid>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={flaggedOnly}
                                    onChange={(e) => setFlaggedOnly(e.target.checked)}
                                />
                            }
                            data-help="attendance.employee.flaggedOnly"
                            label="Flagged only"
                        />
                    </Grid>
                )}
            </Grid>

            {attendanceData && (
                <ResponsiveTable<EmployeeAttendanceRow> data-help-section="attendance.employee.table"
                    pageKey="attendance.employee"
                    columns={employeeAttendanceColumnsFor(hasLocationData, siteNamesById)}
                    rows={rows}
                    getRowKey={r => r.rawDate}
                    emptyMessage="No attendance in this date range"
                />
            )}

            {!attendanceData && !loading && (
                <Typography color="text.secondary">
                    Select an employee and click "Apply Filters" to view attendance.
                </Typography>
            )}

            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} PaperProps={{ 'data-help-page': 'attendance.editForm.page' } as any} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Attendance</DialogTitle>
                <DialogContent data-help-section="attendance.editForm.section.details">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            data-help="attendance.editForm.field.date"
                            label="Date"
                            value={editingAttendance ? format(parseISO(editingAttendance.date), 'dd-MM-yyyy') : ''}
                            disabled
                            fullWidth
                        />
                        <TextField
                            data-help="attendance.employee.editStart"
                            label="Start Time"
                            type="time"
                            value={editStartTime}
                            onChange={(e) => setEditStartTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            data-help="attendance.employee.editEnd"
                            label="End Time"
                            type="time"
                            value={editEndTime}
                            onChange={(e) => setEditEndTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <FormControlLabel
                            data-help="attendance.editForm.field.endsNextDay"
                            control={<Checkbox checked={editEndsNextDay} onChange={(e) => setEditEndsNextDay(e.target.checked)} />}
                            label="Ends next day (clock-out after midnight)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button data-help="attendance.editForm.cancel" onClick={handleCloseEditDialog} disabled={saving}>Cancel</Button>
                    <Button data-help="attendance.editForm.save" onClick={handleSaveEdit} variant="contained" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmployeeAttendanceList;
